/**
 * Generation-based acknowledgement for account resyncs.
 *
 * Why this exists rather than reading the existing loaded/loading atoms:
 * bumping triggerResyncAtom does NOT reset `ordersLoadedAccountIdsAtom`,
 * `balancesLoadedAccountIdsAtom` or `accountDataLoadingAtom` — the sync
 * components only reset their internal dump trackers. So a recovery that starts
 * while a previous dump has already finished would immediately observe
 * `loading === false`, conclude the *new* dump had landed, and report success
 * without a single fresh byte having arrived.
 *
 * Instead each domain acks explicitly, tagged with the generation it is acking,
 * and only once every account in scope has re-dumped.
 */
import store from '@/lib/configs/store-config'
import { triggerResyncAtom } from '@/state/account'

import type { ResyncAckResult, ResyncDomain } from './types'

const ALL_DOMAINS: readonly ResyncDomain[] = ['balances', 'positions', 'orders', 'trades']

type PendingWait = {
  generation: number
  outstanding: Set<ResyncDomain>
  resolve: (result: ResyncAckResult) => void
  timeoutId: ReturnType<typeof setTimeout>
}

/** Acks that arrived for the current generation. */
let acked = new Set<ResyncDomain>()
let pendingWait: PendingWait | null = null

/** The generation acks are currently being matched against. */
export function currentResyncGeneration(): number {
  return store.get(triggerResyncAtom)
}

/**
 * Start a new resync generation.
 *
 * Sole owner of the triggerResyncAtom increment, so there is exactly one place
 * that decides what "generation N" means. MUST be called before restart() — if
 * the socket re-authenticates first, the fresh dump lands while the trackers
 * still hold the previous generation's state, gets treated as a delta, and the
 * ack never fires.
 */
export function beginResync(): number {
  const generation = currentResyncGeneration() + 1

  // A wait still open from an earlier generation can never be satisfied now.
  if (pendingWait !== null) {
    settleWait('timeout')
  }
  acked = new Set()

  store.set(triggerResyncAtom, generation)
  return generation
}

/**
 * Record that one domain has finished re-dumping for `generation`.
 *
 * Acks for a stale generation are dropped: a dump that arrives after its
 * recovery already timed out must not retroactively flip that recovery to
 * success.
 */
export function ackResync(domain: ResyncDomain, generation: number): void {
  if (generation !== currentResyncGeneration()) return

  acked.add(domain)

  if (pendingWait !== null && pendingWait.generation === generation) {
    pendingWait.outstanding.delete(domain)
    if (pendingWait.outstanding.size === 0) settleWait('ok')
  }
}

/**
 * Wait for every domain in `domains` to ack `generation`.
 *
 * Pass only the domains that can actually ack — with no active accounts none of
 * the account streams will ever re-dump, so waiting on them would guarantee a
 * timeout. Callers should start this immediately after beginResync() rather than
 * after restart() resolves, so the restart and ack timeouts overlap instead of
 * stacking.
 */
export function waitForResyncAcks(
  generation: number,
  timeoutMs: number,
  domains: readonly ResyncDomain[] = ALL_DOMAINS
): Promise<ResyncAckResult> {
  if (generation !== currentResyncGeneration()) return Promise.resolve('timeout')

  const outstanding = new Set(domains.filter((domain) => !acked.has(domain)))
  if (outstanding.size === 0) return Promise.resolve('ok')

  // Only one recovery runs at a time; a second wait supersedes the first.
  if (pendingWait !== null) settleWait('timeout')

  return new Promise<ResyncAckResult>((resolve) => {
    const timeoutId = setTimeout(() => settleWait('timeout'), timeoutMs)
    pendingWait = { generation, outstanding, resolve, timeoutId }
  })
}

function settleWait(result: ResyncAckResult): void {
  if (pendingWait === null) return
  const wait = pendingWait
  pendingWait = null
  clearTimeout(wait.timeoutId)
  wait.resolve(result)
}

/** Test seam — drops any in-flight wait without resolving it as success. */
export function resetResyncAcksForTest(): void {
  if (pendingWait !== null) settleWait('timeout')
  acked = new Set()
}
